import { useState } from "react";
import { Download, Euro, Mail, ShoppingBag, Smartphone, Truck } from "lucide-react";
import { formatPrice } from "@/lib/site";

const STAT_ITEMS = [
  { k: "newsletter", l: "Iscritti newsletter", icon: Mail },
  { k: "app", l: "In lista InkMaster", icon: Smartphone },
  { k: "paid_orders", l: "Ordini pagati", icon: ShoppingBag },
  { k: "revenue", l: "Incasso totale", icon: Euro, money: true },
];

const FILTERS = [
  { id: "all", label: "Tutti" },
  { id: "newsletter", label: "Newsletter" },
  { id: "app", label: "App tattoo" },
];

const STATUS = {
  paid: { l: "Pagato", c: "bg-acid text-ink" },
  pending_payment: { l: "In attesa", c: "bg-ink-3 text-smoke" },
  expired: { l: "Scaduto", c: "bg-red-500/15 text-red-400" },
  failed: { l: "Fallito", c: "bg-red-500/15 text-red-400" },
  demo_received: { l: "Demo", c: "bg-ink-3 text-smoke" },
};

const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });

const shipLabel = (s) => {
  if (!s) return "—";
  const a = s.address || {};
  const name = typeof s.name === "string" ? s.name : s.name?.full_name;
  const city = [a.postal_code, a.city || a.admin_area_2].filter(Boolean).join(" ");
  return [name, a.line1 || a.address_line_1, city, a.country || a.country_code].filter(Boolean).join(", ");
};

const th = "px-4 py-3 text-left font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-smoke";
const td = "px-4 py-4 align-top text-sm";

const Empty = ({ text }) => <p className="border border-line p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-smoke">{text}</p>;

export const StatsGrid = ({ stats }) => (
  <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
    {STAT_ITEMS.map(({ k, l, icon: Icon, money }) => (
      <div key={k} className="bg-ink-2 p-6" data-testid={`admin-stat-${k}`}>
        <Icon className="h-5 w-5 text-acid" />
        <p className="mt-6 font-display text-3xl font-black sm:text-4xl">{stats ? (money ? formatPrice(stats[k]) : stats[k]) : "—"}</p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">{l}</p>
      </div>
    ))}
  </div>
);

export const SubscribersPanel = ({ source, setSource, rows, loading, onExport }) => (
  <div>
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setSource(f.id)}
            data-testid={`admin-filter-${f.id}`}
            className={`h-9 border px-4 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${source === f.id ? "border-acid bg-acid text-ink" : "border-line hover:border-bone"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <button onClick={onExport} data-testid="admin-export-csv-button" className="inline-flex h-10 items-center gap-2 rounded-full bg-bone px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink transition-colors hover:bg-acid">
        <Download className="h-3.5 w-3.5" /> Esporta CSV
      </button>
    </div>
    <p className="mt-4 border-l-2 border-acid bg-ink-2 px-4 py-3 text-sm text-bone/70">
      Per inviare una newsletter: esporta il CSV e importalo in Mailchimp o Brevo (Contatti → Importa). Ogni nuovo iscritto riceve già in automatico un'email di benvenuto.
    </p>
    <div className="mt-6 overflow-x-auto border border-line">
      {loading ? <Empty text="Caricamento…" /> : !rows?.length ? <Empty text="Ancora nessun iscritto" /> : (
        <table className="w-full min-w-[560px]" data-testid="admin-subscribers-table">
          <thead className="border-b border-line bg-ink-2"><tr><th className={th}>Email</th><th className={th}>Lista</th><th className={th}>Iscritto il</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-line last:border-b-0" data-testid="admin-subscriber-row">
                <td className={td}>{r.email}</td>
                <td className={td}><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-acid">{r.source === "app" ? "App tattoo" : "Newsletter"}</span></td>
                <td className={`${td} font-mono text-xs text-smoke`}>{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

const ShipControl = ({ order, onShip }) => {
  const [open, setOpen] = useState(false);
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);
  if (order.status !== "paid") return null;
  if (order.shipped_at) {
    return (
      <div data-testid="admin-order-shipped" className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em]">
        <p className="flex items-center gap-1.5 text-acid"><Truck className="h-3 w-3" /> Spedito{order.tracking ? ` · ${order.tracking}` : ""}</p>
        {order.shipped_email_at && <p className="mt-1 text-smoke" data-testid="admin-order-shipped-email">Email inviata al cliente</p>}
      </div>
    );
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="admin-order-ship-button" className="mt-2 flex items-center gap-1.5 border border-line px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-acid hover:text-acid">
        <Truck className="h-3 w-3" /> Segna spedito
      </button>
    );
  }
  const confirm = async () => {
    setBusy(true);
    await onShip(order.id, tracking);
    setBusy(false);
  };
  return (
    <div className="mt-2 flex w-56 flex-col gap-2">
      <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Codice o link tracking (facoltativo)" data-testid="admin-order-tracking-input" className="h-9 border border-line bg-ink px-2 text-xs outline-none focus:border-acid" />
      <button onClick={confirm} disabled={busy} data-testid="admin-order-ship-confirm" className="h-8 bg-acid font-mono text-[10px] uppercase tracking-[0.15em] text-ink disabled:opacity-60">
        {busy ? "Salvo…" : "Conferma spedizione"}
      </button>
    </div>
  );
};

const GELATO = {
  sending: "Gelato · invio in corso",
  sent: "Gelato · inviato",
  demo: "Gelato · simulato (demo)",
  error: "Gelato · errore",
  not_configured: "Gelato · API key mancante",
};

const GelatoState = ({ order, onSend }) => {
  const [busy, setBusy] = useState(false);
  const g = order.gelato;
  const hasGelato = order.items.some((i) => i.gelato_id || i.source === "gelato");
  if (!hasGelato || order.status !== "paid") return null;
  const retry = g && ["error", "not_configured"].includes(g.state);
  const send = async () => {
    setBusy(true);
    await onSend(order.id);
    setBusy(false);
  };
  return (
    <div className="mt-2 space-y-1" data-testid="admin-order-gelato">
      <p className={`font-mono text-[10px] uppercase tracking-[0.12em] ${retry ? "text-amber-200" : "text-cream/80"}`} data-testid="admin-order-gelato-state">
        {g ? GELATO[g.state] || g.state : "Gelato · in attesa"}
        {g?.fulfillment_status ? ` · ${g.fulfillment_status}` : ""}
        {g?.order_type === "draft" ? (g.test ? " · bozza (pagamento di prova)" : " · bozza") : ""}
      </p>
      {g?.error && <p className="max-w-[220px] text-[11px] text-red-300">{g.error}</p>}
      {(retry || !g) && (
        <button onClick={send} disabled={busy} data-testid="admin-order-gelato-send" className="border border-line px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-acid hover:text-acid disabled:opacity-50">
          {busy ? "Invio…" : "Invia a Gelato"}
        </button>
      )}
    </div>
  );
};

export const OrdersPanel = ({ rows, loading, hiddenCount, onShip, onSendGelato }) => (
  <div>
    {hiddenCount > 0 && (
      <p data-testid="admin-hidden-abandoned-note" className="mb-4 border-l-2 border-line bg-ink-2 px-4 py-3 text-sm text-bone/60">
        {hiddenCount} {hiddenCount === 1 ? "ordine abbandonato" : "ordini abbandonati"} (in attesa di pagamento da oltre 24 ore) {hiddenCount === 1 ? "è nascosto" : "sono nascosti"} automaticamente.
      </p>
    )}
  <div className="overflow-x-auto border border-line">
    {loading ? <Empty text="Caricamento…" /> : !rows?.length ? <Empty text="Ancora nessun ordine" /> : (
      <table className="w-full min-w-[980px]" data-testid="admin-orders-table">
        <thead className="border-b border-line bg-ink-2">
          <tr><th className={th}>Ordine</th><th className={th}>Cliente</th><th className={th}>Articoli</th><th className={th}>Totale</th><th className={th}>Metodo</th><th className={th}>Stato</th><th className={th}>Spedizione</th></tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const st = STATUS[o.status] || { l: o.status, c: "bg-ink-3 text-smoke" };
            return (
              <tr key={o.id} className="border-b border-line last:border-b-0" data-testid="admin-order-row">
                <td className={td}><p className="font-mono text-acid">#{o.id.slice(-6).toUpperCase()}</p><p className="mt-1 font-mono text-[11px] text-smoke">{fmtDate(o.created_at)}</p></td>
                <td className={td}><p>{o.name}</p><p className="text-xs text-smoke">{o.email}</p></td>
                <td className={td}>{o.items.map((i) => <p key={`${i.product_id}-${i.size}`} className="text-xs">{i.name} · {i.size} × {i.qty}</p>)}</td>
                <td className={`${td} font-mono`}>{formatPrice(o.total)}</td>
                <td className={`${td} font-mono text-xs uppercase`}>{o.payment_provider}</td>
                <td className={td}>
                  <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${st.c}`}>{st.l}</span>
                  <ShipControl order={o} onShip={onShip} />
                  <GelatoState order={o} onSend={onSendGelato} />
                </td>
                <td className={`${td} max-w-[240px] text-xs text-bone/70`}>{shipLabel(o.shipping_address)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
  </div>
);
